import { toUTCMidnight } from '@cpce/shared';
import { format } from 'date-fns';
import { Calendar as CalendarIcon, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/Button';
import { Calendar } from '@/components/shadcn/Calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/shadcn/Popover';
import { cn } from '@/utils/style';

interface DatePickerProps {
  value?: Date | null;
  onChange?: (date: Date | null) => void;
  className?: string;
  disabled?: boolean;
  error?: string;
  minDate?: Date;
  maxDate?: Date;
}

export function DatePicker(props: DatePickerProps) {
  const { value: date, onChange: setDate, className, disabled, error, minDate, maxDate } = props;
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  const handleOpenChange = (nextOpen: boolean) => {
    clearTimeout(closeTimer.current);
    setOpen(nextOpen);
  };

  const handleSelect = (selected: Date | undefined) => {
    setDate?.(selected ? toUTCMidnight(selected) : null);
    // Brief delay so the picked day visibly highlights before the popover closes
    closeTimer.current = setTimeout(() => setOpen(false), 100);
  };

  const outOfRangeMatchers = [
    ...(minDate ? [{ before: minDate }] : []),
    ...(maxDate ? [{ after: maxDate }] : []),
  ];

  const handleClear = (e: React.MouseEvent | React.PointerEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    e.preventDefault();
    // Use null instead of undefined - react-hook-form treats undefined as "use default value"
    // and won't re-render the Controller, whereas null is an explicit "no value"
    setDate?.(null);
    setOpen(false);
  };

  return (
    <Popover open={disabled ? false : open} onOpenChange={disabled ? undefined : handleOpenChange}>
      <div className={cn('relative w-34', className)}>
        <PopoverTrigger asChild disabled={disabled}>
          <Button
            data-empty={!date}
            className={cn(
              'data-[empty=true]:text-muted-foreground w-full justify-start',
              date && 'pr-8',
              error && 'border-red-500'
            )}
            variant="outline"
            icon={CalendarIcon}
            disabled={disabled}
            aria-invalid={error ? 'true' : undefined}
          >
            {date ? format(date, 'dd/MM/yyyy') : <span>Date</span>}
          </Button>
        </PopoverTrigger>
        {date && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handleClear(e);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.stopPropagation();
                e.preventDefault();
                handleClear(e);
              }
            }}
            onPointerDown={(e) => {
              e.stopPropagation();
            }}
            onMouseDown={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
            className="absolute right-2 top-1/2 -translate-y-1/2 z-10 p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer"
            disabled={disabled}
            aria-label="Effacer la date"
          >
            <X className="size-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 pointer-events-none" />
          </button>
        )}
      </div>
      <PopoverContent className="w-auto p-0">
        <Calendar
          key={date?.getTime() || 'no-date'}
          mode="single"
          selected={date ?? undefined}
          onSelect={handleSelect}
          defaultMonth={date ?? minDate ?? undefined}
          startMonth={minDate ?? new Date(2000, 0)}
          endMonth={maxDate ?? new Date(2100, 11)}
          disabled={outOfRangeMatchers.length > 0 ? outOfRangeMatchers : undefined}
        />
      </PopoverContent>
    </Popover>
  );
}
